import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Card, InputGroup } from "react-bootstrap";
import {
  updatePassword, EmailAuthProvider,
  reauthenticateWithCredential, linkWithCredential,
  GoogleAuthProvider, reauthenticateWithPopup
} from "firebase/auth";
import { auth } from "../Firebase";
import { LuEye, LuEyeOff } from 'react-icons/lu';

const PASSWORD_REGEX = /^(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*\\.]).{8,}$/;

// ── Composant champ mot de passe avec œil ─────────────────────────────────────
const PasswordInput = ({ label, name, value, onChange, placeholder, isInvalid, feedbackMessage }) => {
  const [show, setShow] = useState(false);
  return (
    <Form.Group className="mb-4">
      <Form.Label>{label}</Form.Label>
      <InputGroup hasValidation>
        <Form.Control
          type={show ? "text" : "password"}
          name={name} value={value} onChange={onChange}
          placeholder={placeholder} required
          isInvalid={isInvalid}
          autoComplete={name === "currentPassword" ? "current-password" : "new-password"}
        />
        <InputGroup.Text onClick={() => setShow(s => !s)} style={{ cursor: 'pointer' }}>
          {show ? <LuEyeOff size={20} /> : <LuEye size={20} />}
        </InputGroup.Text>
        <Form.Control.Feedback type="invalid">{feedbackMessage}</Form.Control.Feedback>
      </InputGroup>
    </Form.Group>
  );
};

export default function ChangePassword() {
  const [validated, setValidated]         = useState(false);
  const [showSuccess, setShowSuccess]     = useState(false);
  const [errorMessage, setErrorMessage]   = useState("");
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [isGoogleUser, setIsGoogleUser]   = useState(false);
  const [hasPassword, setHasPassword]     = useState(false);
  const [mode, setMode]                   = useState(''); // 'changer' | 'creer'
  const [passwordData, setPasswordData]   = useState({
    currentPassword: "",
    newPassword:     "",
    confirmPassword: ""
  });
  const [fieldErrors, setFieldErrors]     = useState({});

  // ── Détecte le type de compte au montage ──────────────────────────────────
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const providers   = user.providerData.map(p => p.providerId);
    const isGoogle    = providers.includes('google.com');
    const hasEmailPwd = providers.includes('password');

    setIsGoogleUser(isGoogle);
    setHasPassword(hasEmailPwd);

    // Détermine le mode automatiquement
    if (!isGoogle) {
      setMode('changer'); // compte email → changement classique
    } else if (isGoogle && hasEmailPwd) {
      setMode('changer'); // compte Google + mot de passe lié → changement
    } else {
      setMode('');        // compte Google sans mot de passe → choix à faire
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (name === 'newPassword') {
      setFieldErrors(prev => ({
        ...prev,
        newPassword: PASSWORD_REGEX.test(value) ? null
          : "8 caractères minimum dont 1 majuscule, 1 chiffre et 1 caractère spécial (!@#$%^&*.)",
        confirmPassword: value !== passwordData.confirmPassword && passwordData.confirmPassword
          ? "Les mots de passe ne correspondent pas" : null,
      }));
    }
    if (name === 'confirmPassword') {
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: value !== passwordData.newPassword
          ? "Les mots de passe ne correspondent pas" : null,
      }));
    }
  };

  const validateAll = (avecActuel = true) => {
    const errors = {};
    if (avecActuel && !passwordData.currentPassword)
      errors.currentPassword = "Veuillez saisir votre mot de passe actuel";
    if (!PASSWORD_REGEX.test(passwordData.newPassword))
      errors.newPassword = "8 caractères minimum dont 1 majuscule, 1 chiffre et 1 caractère spécial (!@#$%^&*.)";
    if (passwordData.newPassword !== passwordData.confirmPassword)
      errors.confirmPassword = "Les mots de passe ne correspondent pas";
    if (avecActuel && passwordData.newPassword === passwordData.currentPassword)
      errors.newPassword = "Le nouveau mot de passe doit être différent de l'ancien";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Changer un mot de passe existant ─────────────────────────────────────
  const handleChangerMotDePasse = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateAll(true)) { setValidated(true); return; }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      // Réauthentification selon le provider principal
      if (isGoogleUser && hasPassword) {
        // Compte Google avec mot de passe lié → réauth email
        const credential = EmailAuthProvider.credential(
          user.email, passwordData.currentPassword
        );
        await reauthenticateWithCredential(user, credential);
      } else {
        // Compte email pur → réauth classique
        const credential = EmailAuthProvider.credential(
          user.email, passwordData.currentPassword
        );
        await reauthenticateWithCredential(user, credential);
      }

      await updatePassword(user, passwordData.newPassword);

      setShowSuccess(true);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setFieldErrors({});
      setTimeout(() => setShowSuccess(false), 5000);

    } catch (error) {
      console.error("Erreur changement mot de passe:", error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential')
        setErrorMessage("Le mot de passe actuel est incorrect.");
      else if (error.code === 'auth/requires-recent-login')
        setErrorMessage("Session expirée. Déconnectez-vous et reconnectez-vous.");
      else if (error.code === 'auth/weak-password')
        setErrorMessage("Mot de passe trop faible. Choisissez un mot de passe plus complexe.");
      else
        setErrorMessage("Une erreur est survenue. Réessayez.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Créer un mot de passe pour la première fois (compte Google) ───────────
  const handleCreerMotDePasse = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateAll(false)) { setValidated(true); return; }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      // Réauthentification Google obligatoire avant linkWithCredential
      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(user, provider);

      // Lie un mot de passe email au compte Google existant
      const credential = EmailAuthProvider.credential(
        user.email, passwordData.newPassword
      );
      await linkWithCredential(user, credential);

      setHasPassword(true);
      setMode('changer');
      setShowSuccess(true);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setFieldErrors({});
      setTimeout(() => setShowSuccess(false), 5000);

    } catch (error) {
      console.error("Erreur création mot de passe:", error);
      if (error.code === 'auth/popup-closed-by-user')
        setErrorMessage("Authentification Google annulée.");
      else if (error.code === 'auth/provider-already-linked')
        setErrorMessage("Un mot de passe est déjà associé à ce compte.");
      else if (error.code === 'auth/weak-password')
        setErrorMessage("Mot de passe trop faible. Choisissez un mot de passe plus complexe.");
      else
        setErrorMessage("Une erreur est survenue. Réessayez.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Compte Google sans mot de passe : choix du mode ──────────────────────
  const renderChoixMode = () => (
    <div className="text-center py-3">
      <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🔐</div>
      <h5 style={{ fontWeight: 700 }}>Votre compte est lié à Google</h5>
      <p className="text-muted" style={{ fontSize: '0.9rem' }}>
        Vous vous êtes inscrit avec Google. Vous pouvez soit gérer
        votre mot de passe directement sur Google, soit créer un
        mot de passe SpotyPark pour vous connecter sans Google.
      </p>

      <div className="d-flex flex-column gap-3 mt-4">
        {/* Option 1 — Gérer sur Google */}
        <a
          href="https://myaccount.google.com/security"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline-danger"
          style={{ fontWeight: 600 }}
        >
          <i className="bi bi-google me-2"></i>
          Gérer mon mot de passe Google
        </a>

        {/* Option 2 — Créer un mot de passe SpotyPark */}
        <Button
          style={{ background: '#07a993', border: 'none', fontWeight: 600 }}
          onClick={() => setMode('creer')}
        >
          🔑 Créer un mot de passe SpotyPark
        </Button>
      </div>

      <p className="text-muted mt-3" style={{ fontSize: '0.8rem' }}>
        En créant un mot de passe SpotyPark, vous pourrez vous connecter
        avec votre email ET avec Google.
      </p>
    </div>
  );

  return (
    <Container className="py-5">
      <Card className="shadow-sm">
        <Card.Body className="p-4">
          <h2 className="text-center mb-4">Modifier mon mot de passe</h2>

          {showSuccess && (
            <Alert variant="success" className="mb-4">
              {mode === 'creer' || hasPassword
                ? "🔑 Mot de passe SpotyPark créé avec succès ! Vous pouvez maintenant vous connecter avec votre email."
                : "✅ Votre mot de passe a été modifié avec succès !"}
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" className="mb-4">{errorMessage}</Alert>
          )}

          {/* ── Compte Google sans mot de passe : affiche le choix ─────── */}
          {isGoogleUser && !hasPassword && mode === '' && renderChoixMode()}

          {/* ── Mode création mot de passe (compte Google) ──────────────── */}
          {mode === 'creer' && (
            <>
              <Alert variant="info" style={{ fontSize: '0.88rem' }}>
                🔑 Créer un mot de passe SpotyPark vous permettra de vous connecter
                avec votre email <strong>{auth.currentUser?.email}</strong> en plus de Google.
                Une fenêtre Google s'ouvrira pour confirmer votre identité.
              </Alert>

              <Form noValidate validated={validated} onSubmit={handleCreerMotDePasse}>
                <PasswordInput
                  label="Nouveau mot de passe SpotyPark"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  isInvalid={!!fieldErrors.newPassword}
                  feedbackMessage={fieldErrors.newPassword}
                />
                {!fieldErrors.newPassword && (
                  <Form.Text className="text-muted d-block mt-n3 mb-3" style={{ fontSize: '0.82rem' }}>
                    8 caractères minimum (1 majuscule, 1 chiffre, 1 caractère spécial : !@#$%^&*.)
                  </Form.Text>
                )}

                <PasswordInput
                  label="Confirmer le mot de passe"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  isInvalid={!!fieldErrors.confirmPassword}
                  feedbackMessage={fieldErrors.confirmPassword}
                />

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => { setMode(''); setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" }); setFieldErrors({}); }}
                    style={{ flex: 1 }}
                  >
                    Retour
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ background: '#07a993', border: 'none', fontWeight: 600, flex: 2 }}
                  >
                    {isSubmitting ? "Création en cours..." : "🔑 Créer mon mot de passe"}
                  </Button>
                </div>
              </Form>
            </>
          )}

          {/* ── Mode changement mot de passe classique ───────────────────── */}
          {mode === 'changer' && (
            <Form noValidate validated={validated} onSubmit={handleChangerMotDePasse}>

              {/* Badge compte Google avec mot de passe lié */}
              {isGoogleUser && hasPassword && (
                <Alert variant="info" style={{ fontSize: '0.85rem' }} className="mb-4">
                  <i className="bi bi-google me-2"></i>
                  Votre compte est lié à Google. Entrez votre mot de passe SpotyPark actuel pour le modifier.
                </Alert>
              )}

              <PasswordInput
                label="Mot de passe actuel"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handleChange}
                placeholder="••••••••"
                isInvalid={!!fieldErrors.currentPassword}
                feedbackMessage={fieldErrors.currentPassword}
              />

              <PasswordInput
                label="Nouveau mot de passe"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handleChange}
                placeholder="••••••••"
                isInvalid={!!fieldErrors.newPassword}
                feedbackMessage={fieldErrors.newPassword}
              />
              {!fieldErrors.newPassword && (
                <Form.Text className="text-muted d-block mt-n3 mb-3" style={{ fontSize: '0.82rem' }}>
                  8 caractères minimum (1 majuscule, 1 chiffre, 1 caractère spécial : !@#$%^&*.)
                </Form.Text>
              )}

              <PasswordInput
                label="Confirmer le nouveau mot de passe"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                isInvalid={!!fieldErrors.confirmPassword}
                feedbackMessage={fieldErrors.confirmPassword}
              />

              <div className="d-grid gap-2">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? "Modification en cours..." : "Modifier mon mot de passe"}
                </Button>
              </div>
            </Form>
          )}

        </Card.Body>
      </Card>
    </Container>
  );
}